import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatSliderChange } from '@angular/material/slider';
import { ReplaySubject } from 'rxjs';

import { ScootaversePageService } from 'app/modules/scootaverse/services/scootaverse-page.service';
import { Loggable } from 'app/utils/logging/loggable';
import BadgeQuestion from 'entity/BadgeQuestion';
import EntryRule from 'entity/EntryRule';
import SocialEntryConfiguration from 'entity/SocialEntryConfiguration';

export interface ConditionInterface {
  property?: string;
  operator?: OperatorsInterface;
  value?: string;
}

export interface RuleBlocksInterface {
  id: number;
  conditions: ConditionInterface[];
}

export interface OperatorsInterface {
  key: string;
  operator: string;
  hasValue?: boolean;
}

const OPERATORS_LIB: OperatorsInterface[] = [
  {
    key: 'EQUALS',
    operator: 'equals',
    hasValue: true,
  },
  {
    key: 'DOES_NOT_EQUAL',
    operator: 'does not equal',
    hasValue: true,
  },
  {
    key: 'CONTAINS',
    operator: 'contains',
    hasValue: true,
  },
  {
    key: 'DOES_NOT_CONTAIN',
    operator: 'does not contain',
    hasValue: true,
  },
  {
    key: 'IS_BLANK',
    operator: 'is blank',
    hasValue: false,
  },
  {
    key: 'IS_NOT_BLANK',
    operator: 'is not blank',
    hasValue: false,
  },
  {
    key: 'CS_EQUALS',
    operator: 'equals (case sensitive)',
    hasValue: true,
  },
  {
    key: 'CS_DOES_NOT_EQUAL',
    operator: 'does not equal (case sensitive)',
    hasValue: true,
  },
  {
    key: 'CS_CONTAINS',
    operator: 'contains (case sensitive)',
    hasValue: true,
  },
  {
    key: 'CS_DOES_NOT_CONTAIN',
    operator: 'does not contain (case sensitive)',
    hasValue: true,
  },
];

const MAX_CONDITIONS = 20;

@Component({
  selector: 'entry-flow-group-former',
  templateUrl: './entry-flow-group-former.component.html',
  styleUrls: ['./entry-flow-group-former.component.scss'],
  standalone: false,
})
export class EntryFlowGroupFormerComponent extends Loggable implements OnDestroy, OnInit {
  private destroyed$ = new ReplaySubject<boolean>(1);

  @Input() public scootaverseId?: string;
  @Input() public settings?: SocialEntryConfiguration;
  @Input() public maxGroupSize = 10;
  @Input() public roomBadgeQuestions: BadgeQuestion[] = [];

  @Output() public nextStepClick = new EventEmitter<boolean>();
  @Output() public settingsChange = new EventEmitter<SocialEntryConfiguration>();

  public loading = false;
  public isFooterTransparent = false;

  public operators: OperatorsInterface[];

  public structuredRuleList: RuleBlocksInterface[] = [];
  public lastId = 0;

  protected groupFormerCountSelected = 1;

  public rules: EntryRule[] = [];

  public constructor(private scootaversePageService: ScootaversePageService) {
    super();
    this.setDisplayName('EntryFlowGroupFormerComponent');
    this.operators = OPERATORS_LIB;
  }

  public ngOnInit(): void {
    this.loading = true;
    this.lastId = 0;
    if (this.scootaverseId) {
      this.rules = this.settings?.rules || [];
      this.maxGroupSize = this.settings?.groupSpawnSize || 10;
      this.groupFormerCountSelected = this.settings?.groupFormerCount || 1;

      this.convertRuleEntryToArray();
      this.findNextId();

      this.loading = false;
    } else {
      this.loading = false;
      void this.scootaversePageService.redirectToEntryFlowList();
    }
  }

  private getOperatorItem(key?: string): OperatorsInterface | undefined {
    if (key) {
      return this.operators.find((item) => item.key === key);
    }
  }

  public convertRuleEntryToArray() {
    const blocks: RuleBlocksInterface[] = [];
    let currentBlock: RuleBlocksInterface = { id: 0, conditions: [] };

    if (this.rules.length) {
      this.rules.forEach((rule) => {
        if (rule.operator === 'or') {
          blocks.push(currentBlock);
          currentBlock = { id: currentBlock.id + 1, conditions: [] };
        } else {
          if (rule.property || rule.value) {
            currentBlock.conditions.push({
              property: rule.property,
              operator: this.getOperatorItem(rule.operator),
              value: rule.value,
            });
          }
          if (rule.subRules) {
            rule.subRules.forEach((subRule: EntryRule) => {
              currentBlock.conditions.push({
                property: subRule.property!,
                operator: this.getOperatorItem(subRule.operator),
                value: subRule.value!,
              });
            });
          }
        }
      });

      blocks.push(currentBlock);
    }
    this.structuredRuleList = blocks;
  }

  public convertArrayToEntryRule() {
    let headCondition: EntryRule;
    let subRules: EntryRule[] = [];
    const returnRules: EntryRule[] = [];

    this.structuredRuleList.forEach((block, blockIndex) => {
      block.conditions.forEach((condition, conditionIndex) => {
        const rule = {
          property: condition.property,
          operator: condition.operator?.key,
          value: condition.value?.toString(),
        };

        // If the actual condition is the first of a block, and this is not the first block, add an or operator
        if (blockIndex > 0 && conditionIndex === 0) {
          returnRules.push({ operator: 'or' });
        }

        if (conditionIndex === 0) {
          headCondition = rule;
        } else {
          subRules.push(rule);
        }
      });

      if (subRules.length) {
        headCondition.subRules = subRules;
      }
      returnRules.push(headCondition);

      headCondition = {};
      subRules = [];
    });

    return returnRules;
  }

  public extractContainerIndexFromId(containerId: string) {
    const regex = /(\d+)$/;
    const match = regex.exec(containerId);
    if (match) {
      return parseInt(match[1]) + 1;
    } else {
      return null;
    }
  }

  public drop(event: CdkDragDrop<string[]>) {
    //Drop event has the origin and destination containers, in the format 'ckd_drop_list_x', so we get the x number in the end
    const originId = this.extractContainerIndexFromId(event.previousContainer.id);
    const destinationId = this.extractContainerIndexFromId(event.container.id);
    //That number will be the id of the desired rule block
    const originIndex = this.structuredRuleList.findIndex((ruleBlock) => ruleBlock.id === originId);
    const destinationIndex = this.structuredRuleList.findIndex((ruleBlock) => ruleBlock.id === destinationId);
    if (originIndex !== null && destinationIndex !== null) {
      //Get the respective lists
      const originList = this.structuredRuleList[originIndex];
      const destinationList = this.structuredRuleList[destinationIndex];
      //Move the element from the original index to the new one inside the new container (or inside the same container)
      const toMoveElement = originList.conditions.splice(event.previousIndex, 1)[0];
      destinationList.conditions.splice(event.currentIndex, 0, toMoveElement);

      this.validateLastOperation(originIndex);
    }
  }

  public onGroupSizeChange(event: MatSliderChange): void {
    this.groupFormerCountSelected = Number(event.value);
  }

  public findNextId() {
    let maxId = 0;
    this.structuredRuleList.forEach((item) => {
      if (item.id > maxId) {
        maxId = item.id;
      }
    });

    if (this.lastId >= maxId + 1) {
      return this.lastId + 1;
    } else {
      return maxId + 1;
    }
  }

  public validateLastOperation(containerIndex: number) {
    //Delete the container if container is empty
    if (this.structuredRuleList[containerIndex].conditions.length === 0) {
      this.lastId = this.findNextId() - 1;
      this.structuredRuleList.splice(containerIndex, 1);
    }
  }

  public handleTransitionOperator(action: string, containerIndex: number, itemIndex: number): void {
    if (action === 'or') {
      //Get all items after the or operator
      const newRuleBlock = this.structuredRuleList[containerIndex].conditions.splice(itemIndex);
      //Create a new rule block with all those items
      this.structuredRuleList.splice(containerIndex + 1, 0, { id: this.findNextId(), conditions: newRuleBlock });
      this.validateLastOperation(containerIndex);
    }
  }

  public handleDeleteCondition(containerIndex: number, itemIndex: number) {
    const ruleBlock = this.structuredRuleList[containerIndex].conditions;
    ruleBlock.splice(itemIndex, 1);
    if (ruleBlock.length > 0) {
      this.structuredRuleList[containerIndex].conditions = ruleBlock;
    } else {
      this.validateLastOperation(containerIndex);
    }
  }

  public addCondition(containerIndex: number) {
    this.structuredRuleList[containerIndex].conditions.push({
      property: '',
      operator: undefined,
      value: '',
    });
  }

  public addRule() {
    this.structuredRuleList.push({
      id: this.findNextId(),
      conditions: [
        {
          property: '',
          operator: undefined,
          value: '',
        },
      ],
    });
  }

  public checkMaxConditions() {
    let conditionCount = 0;
    this.structuredRuleList.forEach((ruleBlock) => {
      conditionCount += ruleBlock.conditions.length;
    });
    return conditionCount >= MAX_CONDITIONS;
  }

  public checkMaxConditionsExceeded() {
    let conditionCount = 0;
    this.structuredRuleList.forEach((ruleBlock) => {
      conditionCount += ruleBlock.conditions.length;
    });
    return conditionCount > MAX_CONDITIONS;
  }

  public checkDisabledNext() {
    let validBlocks = true;

    //Check if any condition is invalid
    if (this.structuredRuleList.length) {
      this.structuredRuleList.forEach((block) => {
        block.conditions.forEach((condition) => {
          if (!condition.property) {
            validBlocks = false;
          } else if (!condition.operator) {
            validBlocks = false;
          } else if (condition.operator?.hasValue && !condition.value) {
            validBlocks = false;
          }
        });
      });
    } else {
      validBlocks = false;
    }

    return !validBlocks || this.checkMaxConditionsExceeded();
  }

  public nextStep() {
    this.settingsChange.emit({
      rules: this.convertArrayToEntryRule(),
      groupFormerCount: this.groupFormerCountSelected,
    });
    this.nextStepClick.emit(true);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
